import { supabase, supabaseAdmin } from '../lib/supabase';
import { logAction, getCurrentAdmin } from './logger';

export interface MatchRow {
  id: string;
  home_team: string;
  away_team: string;
  match_date: string;
  match_time: string | null;
  competition: string;
  sport: string;
  is_visible: boolean;
}

export interface TeamRow {
  team_name: string;
  sport: string | null;
  match_count: number;
  is_hidden: boolean;
}

// ─── Matchs ───
export async function listUpcomingMatches(): Promise<MatchRow[]> {
  const today = new Date().toISOString().split('T')[0];
  const { data, error } = await supabase
    .from('matches')
    .select('id, home_team, away_team, match_date, match_time, competition, sport, is_visible')
    .gte('match_date', today)
    .order('match_date')
    .order('match_time')
    .limit(500);
  if (error) throw error;
  return (data || []) as MatchRow[];
}

export async function setMatchVisibility(match: MatchRow, isVisible: boolean): Promise<void> {
  if (match.is_visible === isVisible) return;
  const admin = getCurrentAdmin();
  const { error } = await supabaseAdmin
    .from('matches')
    .update({ is_visible: isVisible })
    .eq('id', match.id);
  if (error) throw error;
  const label = `${match.home_team} vs ${match.away_team}`;
  await logAction(
    'set_match_visibility',
    'match',
    match.id,
    { is_visible: match.is_visible },
    { is_visible: isVisible },
    `${admin?.name || 'admin'} a ${isVisible ? 'rendu visible' : 'masqué'} ${label}`
  );
}

// ─── Équipes ───
export async function listTeams(): Promise<TeamRow[]> {
  // Fetch toutes les équipes distinctes (home + away) depuis les matchs futurs
  const today = new Date().toISOString().split('T')[0];
  const [{ data: matches }, { data: hidden }] = await Promise.all([
    supabase.from('matches').select('home_team, away_team, sport').gte('match_date', today).limit(2000),
    supabase.from('hidden_teams').select('team_name'),
  ]);

  const hiddenSet = new Set((hidden || []).map((h: any) => h.team_name));
  const counts = new Map<string, { sport: string | null; count: number }>();

  for (const m of matches || []) {
    const sport = (m as any).sport || null;
    for (const team of [(m as any).home_team, (m as any).away_team]) {
      if (!team) continue;
      const cur = counts.get(team);
      if (cur) cur.count++;
      else counts.set(team, { sport, count: 1 });
    }
  }

  const rows: TeamRow[] = Array.from(counts.entries()).map(([team_name, v]) => ({
    team_name,
    sport: v.sport,
    match_count: v.count,
    is_hidden: hiddenSet.has(team_name),
  }));
  // Sort : cachées d'abord (rapides à voir), puis par count desc
  rows.sort((a, b) => {
    if (a.is_hidden !== b.is_hidden) return a.is_hidden ? -1 : 1;
    return b.match_count - a.match_count;
  });
  return rows;
}

export async function setTeamHidden(team: TeamRow, hide: boolean): Promise<void> {
  if (team.is_hidden === hide) return;
  const admin = getCurrentAdmin();
  if (hide) {
    const { error } = await supabaseAdmin.from('hidden_teams').insert({
      team_name: team.team_name,
      sport: team.sport,
      hidden_by: admin?.email || 'admin',
    });
    if (error) throw error;
    await logAction(
      'hide_team', 'team', team.team_name,
      null, { hidden: true },
      `${admin?.name || 'admin'} a masqué l'équipe "${team.team_name}"`
    );
  } else {
    const { error } = await supabaseAdmin.from('hidden_teams').delete().eq('team_name', team.team_name);
    if (error) throw error;
    await logAction(
      'unhide_team', 'team', team.team_name,
      { hidden: true }, null,
      `${admin?.name || 'admin'} a réaffiché l'équipe "${team.team_name}"`
    );
  }
}
